/**
 * One typing run, independent of React and of the keyboard layout.
 *
 * A layout turns each target word into UNITS — the smallest piece of text that can be
 * checked on its own. In English a unit is one character; in Bijoy it is a whole
 * syllable cluster, because there the vowel sign ি is typed BEFORE its consonant and
 * the screen only makes sense once the cluster is complete. Each unit lists the
 * keystroke sequences that type it (`options`); the first is the one the guide shows.
 *
 * Two ways to run:
 *  - strict (lessons, drills): a wrong key is counted and refused, the learner must
 *    press the right one — the classic tutor behaviour (Mavis, TypingClub, Bijoy tutor).
 *  - free (speed test, job exam): every key goes in, Backspace corrects, Space moves
 *    to the next word, and the result is scored afterwards like a real typing test.
 *
 * @typedef {{ code: string, shift: boolean }} Stroke
 * @typedef {{ text: string, options: Stroke[][] }} Unit
 * @typedef {{
 *   id: string,
 *   script: 'latin'|'bengali',
 *   encodeWord: (word: string) => Unit[],
 *   render: (strokes: Stroke[]) => string,
 *   strokeLabel: (stroke: Stroke) => string,
 * }} Layout
 */

export const SPACE_STROKE = Object.freeze({ code: 'Space', shift: false });

export const sameStroke = (a, b) => !!a && !!b && a.code === b.code && !!a.shift === !!b.shift;

export const splitWords = (text) => String(text || '').replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * Units for a whole text, words separated by a space unit. `wordIndex` lets the view
 * group the units back into words so a line never breaks inside one.
 */
export function buildUnits(layout, text) {
    const words = splitWords(text);
    const units = [];
    words.forEach((word, wordIndex) => {
        if (wordIndex > 0) units.push({ text: ' ', options: [[SPACE_STROKE]], wordIndex: wordIndex - 1, space: true });
        layout.encodeWord(word).forEach((unit) => units.push({ ...unit, wordIndex }));
    });
    return { words, units };
}

/* ------------------------------------------------------------------ strict */

export function createStrictSession(layout, text) {
    const { words, units } = buildUnits(layout, text);
    return {
        mode: 'strict',
        words,
        units,
        index: 0, // unit being typed
        pressed: [], // strokes already typed inside that unit
        unitErrors: new Uint8Array(units.length), // 1 = a wrong key was pressed on this unit
        log: [], // { t, expected, stroke, ok }
        startedAt: null,
        lastAt: null,
        finishedAt: null,
        flash: 0, // bumps on every wrong key so the view can shake
    };
}

/** Options of the current unit still compatible with what has been typed in it. */
export function liveOptions(session) {
    const unit = session.units[session.index];
    if (!unit) return [];
    const n = session.pressed.length;
    return unit.options.filter((option) => option.length > n
        && session.pressed.every((stroke, i) => sameStroke(stroke, option[i])));
}

/** The keystroke the guide should light up next, or null once finished. */
export function expectedStroke(session) {
    if (session.mode !== 'strict') return null;
    const options = liveOptions(session);
    return options.length ? options[0][session.pressed.length] : null;
}

export function strictPress(session, stroke, at = now()) {
    if (session.finishedAt) return session;
    const options = liveOptions(session);
    if (!options.length) return session;
    const n = session.pressed.length;
    const expected = options[0][n];
    const matching = options.filter((option) => sameStroke(option[n], stroke));
    const next = {
        ...session,
        startedAt: session.startedAt ?? at,
        lastAt: at,
        log: [...session.log, { t: at, prevT: session.lastAt ?? at, expected, stroke, ok: matching.length > 0, unit: session.index }],
    };
    if (!matching.length) {
        const unitErrors = new Uint8Array(session.unitErrors);
        unitErrors[session.index] = 1;
        return { ...next, unitErrors, flash: session.flash + 1 };
    }
    const pressed = [...session.pressed, stroke];
    const done = matching.some((option) => option.length === pressed.length);
    if (!done) return { ...next, pressed };
    const index = session.index + 1;
    return {
        ...next,
        index,
        pressed: [],
        finishedAt: index >= session.units.length ? at : null,
    };
}

/* -------------------------------------------------------------------- free */

export function createFreeSession(layout, text, { allowBackIntoCorrect = false } = {}) {
    const words = splitWords(text);
    return {
        mode: 'free',
        words,
        buffers: words.map(() => []), // strokes typed for each word
        index: 0,
        allowBackIntoCorrect,
        keystrokes: 0,
        corrections: 0,
        log: [],
        startedAt: null,
        lastAt: null,
        finishedAt: null,
        rendered: words.map(() => ''),
    };
}

const normalise = (text) => String(text || '').normalize('NFC');

export function freeWordCorrect(session, i) {
    return normalise(session.rendered[i]) === normalise(session.words[i]);
}

export function freePress(session, layout, stroke, at = now()) {
    if (session.finishedAt) return session;
    const base = { ...session, startedAt: session.startedAt ?? at, lastAt: at, keystrokes: session.keystrokes + 1 };
    const buffers = session.buffers.slice();
    const rendered = session.rendered.slice();
    const i = session.index;

    if (stroke.code === 'Space') {
        if (!buffers[i].length) return session; // a space before typing anything is ignored
        const index = i + 1;
        const finished = index >= session.words.length;
        return {
            ...base,
            log: [...session.log, { t: at, word: i, kind: 'space' }],
            index: finished ? i : index,
            finishedAt: finished ? at : null,
        };
    }

    if (stroke.code === 'Backspace') {
        if (buffers[i].length) {
            buffers[i] = stroke.wholeWord ? [] : buffers[i].slice(0, -1);
            rendered[i] = layout.render(buffers[i]);
            return { ...base, buffers, rendered, corrections: session.corrections + 1 };
        }
        // Step back into the previous word — only when it was typed wrong, the way
        // Monkeytype allows it, so a finished correct word stays done.
        if (i > 0 && (session.allowBackIntoCorrect || !freeWordCorrect(session, i - 1))) {
            return { ...base, index: i - 1, corrections: session.corrections + 1 };
        }
        return session;
    }

    buffers[i] = [...buffers[i], stroke];
    rendered[i] = layout.render(buffers[i]);
    const next = { ...base, buffers, rendered, log: [...session.log, { t: at, word: i, kind: 'key', stroke }] };
    // The last word ends the run the moment it is typed right — nobody presses
    // Space after the final word of a passage.
    if (i === session.words.length - 1 && normalise(rendered[i]) === normalise(session.words[i])) {
        return { ...next, finishedAt: at };
    }
    return next;
}

/** Stop a timed run where it stands. */
export function finishSession(session, at = now()) {
    if (session.finishedAt) return session;
    return { ...session, finishedAt: at, startedAt: session.startedAt ?? at };
}
